"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDateFilterRepresentation = exports.getDateFilterTitleUsingTranslator = exports.getDateFilterTitle = exports.formatRelativeDateRange = exports.formatAbsoluteDateRange = exports.formatAbsoluteDate = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var format_1 = tslib_1.__importDefault(require("date-fns/format"));
var capitalize_1 = tslib_1.__importDefault(require("lodash/capitalize"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var i18n_1 = require("../../constants/i18n");
var DateConversions_1 = require("../DateConversions");
var sdk_backend_spi_1 = require("@gooddata/sdk-backend-spi");
var Platform_1 = require("../../constants/Platform");
exports.formatAbsoluteDate = function (date, dateFormat) { return format_1.default(date, dateFormat); };
exports.formatAbsoluteDateRange = function (from, to, dateFormat) {
    var fromDate = DateConversions_1.convertPlatformDateStringToDate(from);
    var toDate = DateConversions_1.convertPlatformDateStringToDate(to);
    var fromTitle = exports.formatAbsoluteDate(fromDate, dateFormat);
    var toTitle = exports.formatAbsoluteDate(toDate, dateFormat);
    if (isEqual_1.default(fromTitle, toTitle)) {
        return fromTitle;
    }
    return fromTitle + "\u2013" + toTitle;
};
var relativeDateRangeFormatters = [
    {
        // Today, This month
        predicate: function (from, to) { return from === 0 && to === 0; },
        formatter: function (_from, _to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.this" + capitalize_1.default(intlGranularity) + ".title" });
        },
    },
    {
        // Tomorrow, Next month
        predicate: function (from, to) { return from === 1 && to === 1; },
        formatter: function (_from, _to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.next" + capitalize_1.default(intlGranularity) + ".title" });
        },
    },
    {
        // Yesterday, Last month
        predicate: function (from, to) { return from === -1 && to === -1; },
        formatter: function (_from, _to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.last" + capitalize_1.default(intlGranularity) + ".title" });
        },
    },
    {
        // Next N days (months)
        predicate: function (from) { return from === 0; },
        formatter: function (_from, to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.nextN" + capitalize_1.default(intlGranularity) + "s" }, { n: Math.abs(to) + 1 });
        },
    },
    {
        // Last N days (months)
        predicate: function (_from, to) { return to === 0; },
        formatter: function (from, _to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.lastN" + capitalize_1.default(intlGranularity) + "s" }, { n: Math.abs(from) + 1 });
        },
    },
    {
        // From N days ago to N days ago
        predicate: function (from, to) { return from < 0 && from === to; },
        formatter: function (from, _to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.interval." + intlGranularity + "s.past.sameValue" }, { value: Math.abs(from) });
        },
    },
    {
        // From N days ago to N days ahead
        predicate: function (from, to) { return from > 0 && from === to; },
        formatter: function (from, _to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.interval." + intlGranularity + "s.future.sameValue" }, { value: Math.abs(from) });
        },
    },
    {
        // From N days ago to M days ago
        predicate: function (from, to) { return from < 0 && to < 0; },
        formatter: function (from, to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.interval." + intlGranularity + "s.past" }, {
                from: Math.abs(from),
                to: Math.abs(to),
            });
        },
    },
    {
        // From N days ahead to M days ahead
        predicate: function (from, to) { return from > 0 && to > 0; },
        formatter: function (from, to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.interval." + intlGranularity + "s.future" }, {
                from: Math.abs(from),
                to: Math.abs(to),
            });
        },
    },
    {
        // From N days ago to M days ahead
        predicate: function () { return true; },
        formatter: function (from, to, intlGranularity, translator) {
            return translator.formatMessage({ id: "filters.interval." + intlGranularity + "s.mixed" }, {
                from: Math.abs(from),
                to: Math.abs(to),
            });
        },
    },
];
exports.formatRelativeDateRange = function (from, to, granularity, translator) {
    var intlGranularity = i18n_1.granularityIntlCodes[granularity];
    var formatter = relativeDateRangeFormatters.find(function (f) { return f.predicate(from, to); }).formatter;
    return formatter(from, to, intlGranularity, translator);
};
var getAllTimeFilterRepresentation = function (translator) {
    return translator.formatMessage({ id: "filters.allTime.title" });
};
var getAbsoluteFormFilterRepresentation = function (filter, dateFormat) {
    return filter.from && filter.to ? exports.formatAbsoluteDateRange(filter.from, filter.to, dateFormat) : "";
};
var getAbsolutePresetFilterRepresentation = function (filter, dateFormat) { return exports.formatAbsoluteDateRange(filter.from, filter.to, dateFormat); };
var getRelativeFormFilterRepresentation = function (filter, translator) {
    return typeof filter.from === "number" && typeof filter.to === "number"
        ? exports.formatRelativeDateRange(filter.from, filter.to, filter.granularity, translator)
        : "";
};
var getRelativePresetFilterRepresentation = function (filter, translator) { return exports.formatRelativeDateRange(filter.from, filter.to, filter.granularity, translator); };
var getDateFilterRepresentationByFilterType = function (filter, translator, dateFormat) {
    if (sdk_backend_spi_1.isAbsoluteDateFilterForm(filter) || sdk_backend_spi_1.isRelativeDateFilterForm(filter)) {
        return getDateFilterRepresentationUsingTranslator(filter, translator, dateFormat);
    }
    else if (sdk_backend_spi_1.isAllTimeDateFilterOption(filter) ||
        sdk_backend_spi_1.isAbsoluteDateFilterPreset(filter) ||
        sdk_backend_spi_1.isRelativeDateFilterPreset(filter)) {
        return filter.name || getDateFilterRepresentationUsingTranslator(filter, translator, dateFormat);
    }
    else {
        throw new Error("Unknown DateFilterOption type");
    }
};
// excludeCurrentPeriod is extra metadata that is needed by translation, but it is only used by relative filters
// so the data structure is little inconsistent - for example when we translate absoluteForm we need to pass
// excludeCurrentPeriod that is completely unrelated to absolute filter and is not used in absolute translations.
// So in the future, if there will be need for more metadata, consider adding wrapper union type that would wrap
// DateFilterOption along with additional metadata related to given filter. eg.:
// | { filter: IRelativeDateFilterPreset, excludeCurrentPeriod: boolean } |
// | { filter: IAbsoluteFilterForm } |
// ...
/**
 * Gets the filter title favoring custom name if specified.
 * @returns Representation of the filter (e.g. "My preset", "From 2 weeks ago to 1 week ahead")
 */
exports.getDateFilterTitle = function (filter, locale, dateFormat) {
    if (dateFormat === void 0) { dateFormat = Platform_1.DEFAULT_DATE_FORMAT; }
    var translator = sdk_ui_1.getIntl(locale);
    return getDateFilterRepresentationByFilterType(filter, translator, dateFormat);
};
/**
 * Gets the filter title favoring custom name if specified. This function is only for mock purpose.
 * @returns Representation of the filter (e.g. "My preset", "From 2 weeks ago to 1 week ahead")
 */
exports.getDateFilterTitleUsingTranslator = function (filter, translator, dateFormat) {
    if (dateFormat === void 0) { dateFormat = Platform_1.DEFAULT_DATE_FORMAT; }
    return getDateFilterRepresentationByFilterType(filter, translator, dateFormat);
};
/**
 * Gets the filter representation regardless of custom name.
 * @returns Representation of the filter (e.g. "From 2 weeks ago to 1 week ahead")
 */
var getDateFilterRepresentationUsingTranslator = function (filter, translator, dateFormat) {
    if (sdk_backend_spi_1.isAbsoluteDateFilterForm(filter)) {
        return getAbsoluteFormFilterRepresentation(filter, dateFormat);
    }
    else if (sdk_backend_spi_1.isAbsoluteDateFilterPreset(filter)) {
        return getAbsolutePresetFilterRepresentation(filter, dateFormat);
    }
    else if (sdk_backend_spi_1.isAllTimeDateFilterOption(filter)) {
        return getAllTimeFilterRepresentation(translator);
    }
    else if (sdk_backend_spi_1.isRelativeDateFilterForm(filter)) {
        return getRelativeFormFilterRepresentation(filter, translator);
    }
    else if (sdk_backend_spi_1.isRelativeDateFilterPreset(filter)) {
        return getRelativePresetFilterRepresentation(filter, translator);
    }
    else {
        throw new Error("Unknown DateFilterOption type");
    }
};
exports.getDateFilterRepresentation = function (filter, locale, dateFormat) {
    if (dateFormat === void 0) { dateFormat = Platform_1.DEFAULT_DATE_FORMAT; }
    var translator = sdk_ui_1.getIntl(locale);
    return getDateFilterRepresentationUsingTranslator(filter, translator, dateFormat);
};
//# sourceMappingURL=DateFilterTitle.js.map